// Data Loading Functions for Table Editor

import { studyTableTo2DArray, documentExtractTableTo2DArray, referenceTo2DArray } from './data-conversion.js';

export type FlowMode = 'source-document' | 'source-study' | 'document-study';

export interface DataLoadingContext {
  documentExtractStore: any;
  documentExtractFullStore: any;
  studyExtractStore: any;
  projectStructureStore: any;
  topicId: string;
  tableId: string;
  referenceId?: string; // Optional: for reference mode
}

export function loadSourceDocumentData(context: DataLoadingContext) {
  
  // Load document extract topics as target
  if (context.documentExtractFullStore && context.projectStructureStore && context.topicId && context.tableId) {
    const targetData2D = documentExtractTableTo2DArray(
      context.topicId, 
      context.tableId, 
      context.documentExtractFullStore, 
      context.projectStructureStore
    );
    return targetData2D;
  }
  return [];
}

export function loadSourceStudyData(context: DataLoadingContext) {
  // Current implementation - load study extract topics as target
  if (context.studyExtractStore && context.projectStructureStore && context.topicId && context.tableId) {
    const targetData2D = studyTableTo2DArray(
      context.topicId, 
      context.tableId, 
      context.studyExtractStore, 
      context.projectStructureStore
    );
    return targetData2D;
  }
  return [];
}

export function loadDocumentStudyData(context: DataLoadingContext) {
  // Load both document and study extract topics
  if (context.documentExtractFullStore && context.studyExtractStore && context.projectStructureStore && context.topicId && context.tableId) {
    const documentData = documentExtractTableTo2DArray(
      context.topicId, 
      context.tableId, 
      context.documentExtractFullStore, 
      context.projectStructureStore
    );
    
    const studyData = studyTableTo2DArray(
      context.topicId, 
      context.tableId, 
      context.studyExtractStore, 
      context.projectStructureStore
    );
    
    return { documentData, studyData };
  }
  return { documentData: [], studyData: [] };
}

export function loadSourceDocumentDataForReference(context: DataLoadingContext) {
  
  // Load document extract references as target
  if (context.documentExtractFullStore && context.projectStructureStore && context.referenceId) {
    const targetData2D = referenceTo2DArray(
      context.referenceId,
      context.documentExtractFullStore,
      context.projectStructureStore
    );
    return targetData2D;
  }
  return [];
}

export function loadSourceStudyDataForReference(context: DataLoadingContext) {
  // Load study extract references as target
  if (context.studyExtractStore && context.projectStructureStore && context.referenceId) {
    const targetData2D = referenceTo2DArray(
      context.referenceId,
      context.studyExtractStore,
      context.projectStructureStore
    );
    return targetData2D;
  }
  return [];
}

export function loadDocumentStudyDataForReference(context: DataLoadingContext) {
  // Load both document and study extract references
  if (context.documentExtractFullStore && context.studyExtractStore && context.projectStructureStore && context.referenceId) {
    const documentData = referenceTo2DArray(
      context.referenceId,
      context.documentExtractFullStore,
      context.projectStructureStore
    );
    
    const studyData = referenceTo2DArray(
      context.referenceId,
      context.studyExtractStore,
      context.projectStructureStore
    );
    
    return { documentData, studyData };
  }
  return { documentData: [], studyData: [] };
}

export function loadDataForMode(mode: FlowMode, context: DataLoadingContext) {
  // Check if we're in reference mode
  if (context.referenceId) {
    switch (mode) {
      case 'source-document':
        return loadSourceDocumentDataForReference(context);
      case 'source-study':
        return loadSourceStudyDataForReference(context);
      case 'document-study':
        return loadDocumentStudyDataForReference(context);
      default:
        return [];
    }
  }
  
  // Topic mode (existing logic)
  switch (mode) {
    case 'source-document':
      return loadSourceDocumentData(context);
    case 'source-study':
      return loadSourceStudyData(context);
    case 'document-study':
      return loadDocumentStudyData(context);
    default:
      return [];
  }
}
